import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, 
  Target, 
  BarChart3, 
  Calendar,
  CheckCircle,
  Percent,
  Activity,
  Zap,
  Shield,
  Flame,
  Scale,
  ArrowRight,
  Trophy,
  LineChart,
  Eye,
  Lock,
  Sparkles,
  ChevronRight,
  Star,
  Wallet,
  DollarSign,
  Award,
  TrendingDown,
  Coins
} from 'lucide-react';
import axios from 'axios';
import PublicTrackRecordJournal from '../components/PublicTrackRecordJournal';
import EstimatedFeesField from '../components/EstimatedFeesField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackRecord = () => {
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrackRecord();
  }, []);

  const fetchTrackRecord = async () => {
    try {
      const response = await axios.get(`${API}/track-record/public`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch track record:', error);
      setStats({
        total_analyses: 0,
        period_start: null,
        period_end: null,
        win_rate_tp1: 0,
        win_rate_tp2: 0,
        win_rate_tp100: 0,
        loss_rate: 0,
        avg_rr: 0,
        by_type: { crypto: { total: 0, wins: 0 }, stocks: { total: 0, wins: 0 } },
        by_profile: { conservative: { total: 0, wins: 0 }, balanced: { total: 0, wins: 0 }, dynamic: { total: 0, wins: 0 } },
        recent_performance: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Hero Stats Component with animation
  const HeroStat = ({ value, label, suffix = '', color = 'text-primary', delay = 0 }) => (
    <div 
      className="text-center animate-fade-in"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={`text-4xl sm:text-5xl lg:text-6xl font-bold font-mono ${color}`}>
        {value}{suffix}
      </div>
      <div className="text-sm sm:text-base text-muted-foreground mt-2">{label}</div>
    </div>
  );

  // Progress Ring Component
  const ProgressRing = ({ percentage, size = 120, strokeWidth = 8, color = 'stroke-primary' }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-white/5"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={`${color} transition-all duration-1000 ease-out`}
        />
      </svg>
    );
  };

  // Win Rate Card with Ring
  const WinRateRing = ({ label, rate, icon: Icon, color, bgColor, borderColor }) => (
    <div className={`relative p-6 rounded-2xl ${bgColor} border ${borderColor} text-center group hover:scale-105 transition-transform duration-300`}>
      <div className="relative w-28 h-28 mx-auto mb-4">
        <ProgressRing percentage={rate} size={112} color={color.replace('text-', 'stroke-')} />
        <div className="absolute inset-0 flex items-center justify-center">
          <div>
            <div className={`text-2xl font-bold ${color}`}>{rate.toFixed(0)}%</div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="font-semibold">{label}</span>
      </div>
      <p className="text-xs text-muted-foreground">
        {language === 'fr' ? 'Taux de réussite' : 'Win rate'}
      </p>
    </div>
  );

  // Financial stat card for Portfolio & Performance section
  const StatCard = ({ icon: Icon, label, value, color = 'text-foreground', sub, ...rest }) => (
    <div
      className="p-5 rounded-2xl bg-card/60 border border-white/10 hover:border-white/20 transition-colors"
      {...rest}
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span>{label}</span>
      </div>
      <div className={`text-2xl sm:text-3xl font-mono font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">
            {language === 'fr' ? 'Chargement des statistiques...' : 'Loading statistics...'}
          </p>
        </div>
      </div>
    );
  }

  const hasData = stats && stats.total_analyses > 0;

  // Calculate overall win rate (trades with positive PnL)
  const overallWinRate = hasData ? (100 - stats.loss_rate) : 0;

  return (
    <div className="min-h-[calc(100vh-64px)]">
      {/* Hero Section - Full Width Gradient */}
      <section className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute top-20 right-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          {/* Header Badge */}
          <div className="text-center mb-8">
            <Badge className="bg-primary/20 text-primary border-primary/30 px-4 py-2 text-sm">
              <Activity className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Performances vérifiées en temps réel' : 'Real-time verified performance'}
            </Badge>
          </div>

          {/* Main Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-center mb-6">
            {language === 'fr' ? 'Notre' : 'Our'}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-400">
              Track Record
            </span>
            <br />
            {language === 'fr' ? 'parle de lui-même' : 'speaks for itself'}
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground text-center max-w-3xl mx-auto mb-12">
            {language === 'fr' 
              ? 'Transparence totale. Chaque analyse IA est trackée et mesurée. Découvrez nos performances réelles, sans artifice.' 
              : 'Total transparency. Every AI analysis is tracked and measured. Discover our real performance, no tricks.'}
          </p>

          {hasData ? (
            <>
              {/* Hero Stats Row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-4xl mx-auto mb-8">
                <HeroStat 
                  value={stats.total_analyses} 
                  label={language === 'fr' ? 'Analyses IA' : 'AI Analyses'}
                  color="text-primary"
                  delay={0}
                />
                <HeroStat 
                  value={overallWinRate.toFixed(0)} 
                  suffix="%" 
                  label={language === 'fr' ? 'Trades gagnants' : 'Winning trades'}
                  color="text-green-400"
                  delay={100}
                />
                <HeroStat 
                  value={stats.win_rate_tp100.toFixed(0)} 
                  suffix="%" 
                  label={language === 'fr' ? 'TP Final atteint' : 'Final TP hit'}
                  color="text-emerald-400"
                  delay={200}
                />
                <HeroStat 
                  value={stats.avg_rr.toFixed(1)} 
                  suffix=":1" 
                  label={language === 'fr' ? 'R:R Moyen' : 'Average R:R'}
                  color="text-orange-400"
                  delay={300}
                />
              </div>

              {/* Period Info — highlighted as a key marketing point */}
              {(() => {
                if (!stats.period_start || !stats.period_end) return null;
                const start = new Date(stats.period_start);
                const end = new Date(stats.period_end);
                const monthNames = language === 'fr'
                  ? ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre']
                  : ['January','February','March','April','May','June','July','August','September','October','November','December'];
                const fmtLong = (d) => `${d.getDate()} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
                const diffMs = end - start;
                const days = Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)));
                const months = Math.round((days / 30) * 10) / 10;
                const monthLabel = language === 'fr'
                  ? (months < 1 ? `${days} jours` : months < 2 ? `${months.toFixed(1)} mois` : `${Math.round(months)} mois`)
                  : (months < 1 ? `${days} days` : months < 2 ? `${months.toFixed(1)} months` : `${Math.round(months)} months`);
                return (
                  <div className="max-w-3xl mx-auto" data-testid="track-record-period">
                    <div className="glass rounded-2xl border border-primary/25 overflow-hidden bg-gradient-to-r from-primary/10 via-emerald-500/5 to-primary/10">
                      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-primary/10">
                        <div className="p-5 text-center">
                          <div className="text-xs text-muted-foreground uppercase tracking-widest mb-1">
                            {language === 'fr' ? 'Début' : 'Start'}
                          </div>
                          <div className="text-base sm:text-lg font-semibold text-foreground">{fmtLong(start)}</div>
                        </div>
                        <div className="p-5 text-center bg-primary/5">
                          <div className="text-xs text-muted-foreground uppercase tracking-widest mb-1">
                            {language === 'fr' ? 'Durée' : 'Duration'}
                          </div>
                          <div className="text-2xl sm:text-3xl font-bold font-mono text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-400">
                            {monthLabel}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {language === 'fr' ? `${days} jours de tracking` : `${days} days of tracking`}
                          </div>
                        </div>
                        <div className="p-5 text-center">
                          <div className="text-xs text-muted-foreground uppercase tracking-widest mb-1">
                            {language === 'fr' ? 'Fin' : 'End'}
                          </div>
                          <div className="text-base sm:text-lg font-semibold text-foreground">{fmtLong(end)}</div>
                        </div>
                      </div>
                    </div>
                    <p className="text-center text-xs text-muted-foreground mt-3">
                      <Calendar className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                      {language === 'fr'
                        ? `${stats.total_analyses} analyses IA proposées sur ${monthLabel} — soit ~${(stats.total_analyses / Math.max(1, months)).toFixed(0)} par mois`
                        : `${stats.total_analyses} AI analyses over ${monthLabel} — ~${(stats.total_analyses / Math.max(1, months)).toFixed(0)} per month`}
                    </p>
                  </div>
                );
              })()}
            </>
          ) : (
            /* Testing Phase Banner */
            <div className="max-w-2xl mx-auto">
              <Card className="bg-gradient-to-br from-primary/10 to-emerald-500/10 border-primary/30 overflow-hidden">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                    <LineChart className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">
                    {language === 'fr' ? 'Phase de test en cours' : 'Testing phase in progress'}
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    {language === 'fr' 
                      ? 'Notre IA est actuellement en phase de validation. Nous testons intensivement avec des analyses quotidiennes pour garantir des résultats fiables.' 
                      : 'Our AI is currently in validation phase. We are intensively testing with daily analyses to ensure reliable results.'}
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      {language === 'fr' ? 'Analyses quotidiennes' : 'Daily analyses'}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      {language === 'fr' ? 'Multi-marchés' : 'Multi-markets'}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      {language === 'fr' ? 'Concepts ICT' : 'ICT Concepts'}
                    </div>
                  </div>
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <p className="text-sm text-muted-foreground mb-4">
                      {language === 'fr' 
                        ? 'Objectif : 150+ trades validés avant lancement officiel' 
                        : 'Goal: 150+ validated trades before official launch'}
                    </p>
                    <Link to="/subscribe">
                      <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                        {language === 'fr' ? 'Être notifié du lancement' : 'Get notified at launch'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>

      {hasData && (
        <>
          {/* Portfolio & Financial Performance */}
          <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent to-black/20">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  {language === 'fr' ? 'Portefeuille & Performance' : 'Portfolio & Performance'}
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  {language === 'fr'
                    ? 'Vue complète du portefeuille paper de suivi : capital initial, PnL cumulé et ratios clés.'
                    : 'Complete view of the tracking paper portfolio: initial capital, cumulative PnL and key ratios.'}
                </p>
                <div className="mt-4 flex justify-center" data-testid="trackrecord-fees-wrapper">
                  <EstimatedFeesField />
                </div>
              </div>

              {/* Row 1 — Capital + PnL */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6" data-testid="financial-stats-row">
                <StatCard
                  icon={Wallet}
                  label={language === 'fr' ? 'Capital initial' : 'Initial capital'}
                  value={`${(stats.initial_capital || 0).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}$`}
                  color="text-primary"
                  data-testid="stat-initial-capital"
                />
                <StatCard
                  icon={Coins}
                  label={language === 'fr' ? 'Capital actuel' : 'Current capital'}
                  value={`${(stats.current_capital || 0).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}$`}
                  color={
                    (stats.current_capital || 0) >= (stats.initial_capital || 0)
                      ? 'text-emerald-400'
                      : 'text-red-400'
                  }
                  data-testid="stat-current-capital"
                />
                <StatCard
                  icon={DollarSign}
                  label={language === 'fr' ? 'PnL total' : 'Total PnL'}
                  value={`${(stats.total_pnl || 0) > 0 ? '+' : ''}${(stats.total_pnl || 0).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}$`}
                  color={(stats.total_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}
                  data-testid="stat-total-pnl"
                />
                <StatCard
                  icon={Award}
                  label="Profit Factor"
                  value={
                    stats.profit_factor === null
                      ? '∞'
                      : (stats.profit_factor || 0).toFixed(2)
                  }
                  color={
                    stats.profit_factor === null || (stats.profit_factor || 0) >= 1.5
                      ? 'text-emerald-400'
                      : (stats.profit_factor || 0) >= 1
                      ? 'text-yellow-400'
                      : 'text-red-400'
                  }
                  sub={
                    stats.profit_factor === null
                      ? language === 'fr'
                        ? 'Aucune perte'
                        : 'No losses'
                      : (stats.profit_factor || 0) >= 2
                      ? language === 'fr' ? 'Excellent' : 'Excellent'
                      : (stats.profit_factor || 0) >= 1.5
                      ? language === 'fr' ? 'Bon' : 'Good'
                      : (stats.profit_factor || 0) >= 1
                      ? language === 'fr' ? 'Moyen' : 'Average'
                      : language === 'fr' ? 'À améliorer' : 'Needs work'
                  }
                  data-testid="stat-profit-factor"
                />
              </div>

              {/* Row 2 — Gains / Losses breakdown */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <StatCard
                  icon={TrendingUp}
                  label={language === 'fr' ? 'Gain moyen' : 'Average win'}
                  value={`+${(stats.avg_win || 0).toFixed(2)}$`}
                  color="text-emerald-400"
                  sub={`${stats.wins_count || 0} ${language === 'fr' ? 'trades gagnants' : 'winning trades'}`}
                  data-testid="stat-avg-win"
                />
                <StatCard
                  icon={TrendingDown}
                  label={language === 'fr' ? 'Perte moyenne' : 'Average loss'}
                  value={`-${(stats.avg_loss || 0).toFixed(2)}$`}
                  color="text-red-400"
                  sub={`${stats.losses_count || 0} ${language === 'fr' ? 'trades perdants' : 'losing trades'}`}
                  data-testid="stat-avg-loss"
                />
                <StatCard
                  icon={TrendingUp}
                  label={language === 'fr' ? 'Total gains' : 'Total gains'}
                  value={`+${(stats.total_gains || 0).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}$`}
                  color="text-emerald-400"
                  data-testid="stat-total-gains"
                />
                <StatCard
                  icon={TrendingDown}
                  label={language === 'fr' ? 'Total pertes' : 'Total losses'}
                  value={`-${(stats.total_losses || 0).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}$`}
                  color="text-red-400"
                  data-testid="stat-total-losses"
                />
              </div>

              {/* ROI badge (Return on Initial Capital) */}
              {stats.initial_capital > 0 && (
                <div className="mt-8 flex justify-center">
                  <Badge
                    className={`text-base sm:text-lg px-4 py-2 ${
                      (stats.total_pnl || 0) >= 0
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                        : 'bg-red-500/10 text-red-400 border-red-500/30'
                    }`}
                    data-testid="stat-roi"
                  >
                    <Percent className="w-4 h-4 mr-2" />
                    ROI :{' '}
                    <span className="font-mono font-bold ml-1">
                      {(stats.total_pnl || 0) >= 0 ? '+' : ''}
                      {((stats.total_pnl / stats.initial_capital) * 100).toFixed(1)}%
                    </span>
                  </Badge>
                </div>
              )}
            </div>
          </section>

          {/* Win Rates by Target Section */}
          <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent to-black/20">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  {language === 'fr' ? 'Performance par Objectif' : 'Performance by Target'}
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  {language === 'fr' 
                    ? 'Notre IA définit 3 niveaux de Take Profit pour chaque trade, permettant une gestion flexible des gains.' 
                    : 'Our AI defines 3 Take Profit levels for each trade, allowing flexible profit management.'}
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-6 lg:gap-8">
                <WinRateRing 
                  label="TP1 (25%)"
                  rate={stats.win_rate_tp1}
                  icon={Target}
                  color="text-green-400"
                  bgColor="bg-green-500/5"
                  borderColor="border-green-500/20"
                />
                <WinRateRing 
                  label="TP2 (50%)"
                  rate={stats.win_rate_tp2}
                  icon={TrendingUp}
                  color="text-primary"
                  bgColor="bg-primary/5"
                  borderColor="border-primary/20"
                />
                <WinRateRing 
                  label="TP100 (100%)"
                  rate={stats.win_rate_tp100}
                  icon={Trophy}
                  color="text-orange-400"
                  bgColor="bg-orange-500/5"
                  borderColor="border-orange-500/20"
                />
              </div>

              {/* Explanation Cards */}
              <div className="mt-12 grid sm:grid-cols-3 gap-4 text-sm">
                <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/10">
                  <div className="flex items-center gap-2 mb-2 text-green-400 font-semibold">
                    <Target className="w-4 h-4" />
                    TP1 - {language === 'fr' ? 'Sécurisation' : 'Securing'}
                  </div>
                  <p className="text-muted-foreground">
                    {language === 'fr' 
                      ? 'Premier objectif conservateur. Permet de sécuriser 25% des gains rapidement.' 
                      : 'First conservative target. Allows securing 25% of gains quickly.'}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
                  <div className="flex items-center gap-2 mb-2 text-primary font-semibold">
                    <TrendingUp className="w-4 h-4" />
                    TP2 - {language === 'fr' ? 'Profit' : 'Profit'}
                  </div>
                  <p className="text-muted-foreground">
                    {language === 'fr' 
                      ? 'Objectif intermédiaire. La moitié de la position est prise ici.' 
                      : 'Intermediate target. Half of the position is taken here.'}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-orange-500/5 border border-orange-500/10">
                  <div className="flex items-center gap-2 mb-2 text-orange-400 font-semibold">
                    <Trophy className="w-4 h-4" />
                    TP100 - {language === 'fr' ? 'Objectif Final' : 'Final Target'}
                  </div>
                  <p className="text-muted-foreground">
                    {language === 'fr' 
                      ? 'Objectif maximum basé sur l\'analyse technique complète.' 
                      : 'Maximum target based on complete technical analysis.'}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Performance by Market */}
          <section className="py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  {language === 'fr' ? 'Performance par Marché' : 'Performance by Market'}
                </h2>
                <p className="text-muted-foreground">
                  {language === 'fr' 
                    ? 'Notre IA analyse tous les marchés avec les mêmes concepts ICT éprouvés.' 
                    : 'Our AI analyzes all markets with the same proven ICT concepts.'}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Crypto */}
                <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20 overflow-hidden group hover:border-yellow-500/40 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                          <Zap className="w-6 h-6 text-yellow-400" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">Crypto</h3>
                          <p className="text-xs text-muted-foreground">{stats.by_type.crypto?.total || 0} {language === 'fr' ? 'analyses' : 'analyses'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-4xl font-bold text-yellow-400 mb-2">
                      {stats.by_type.crypto?.total > 0 
                        ? ((stats.by_type.crypto.wins / stats.by_type.crypto.total) * 100).toFixed(0) 
                        : 0}%
                    </div>
                    <div className="h-2 bg-yellow-500/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full transition-all duration-1000"
                        style={{ width: `${stats.by_type.crypto?.total > 0 ? (stats.by_type.crypto.wins / stats.by_type.crypto.total) * 100 : 0}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Stocks */}
                <Card className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-500/20 overflow-hidden group hover:border-blue-500/40 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                          <BarChart3 className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{language === 'fr' ? 'Actions' : 'Stocks'}</h3>
                          <p className="text-xs text-muted-foreground">{stats.by_type.stocks?.total || 0} {language === 'fr' ? 'analyses' : 'analyses'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-4xl font-bold text-blue-400 mb-2">
                      {stats.by_type.stocks?.total > 0 
                        ? ((stats.by_type.stocks.wins / stats.by_type.stocks.total) * 100).toFixed(0) 
                        : 0}%
                    </div>
                    <div className="h-2 bg-blue-500/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-1000"
                        style={{ width: `${stats.by_type.stocks?.total > 0 ? (stats.by_type.stocks.wins / stats.by_type.stocks.total) * 100 : 0}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Autres (Commodities + ETF + Indices grouped) */}
                {(() => {
                  const bt = stats.by_type || {};
                  const otherTotal = (bt.commodities?.total || 0) + (bt.etf?.total || 0) + (bt.indices?.total || 0);
                  const otherWins = (bt.commodities?.wins || 0) + (bt.etf?.wins || 0) + (bt.indices?.wins || 0);
                  const otherPct = otherTotal > 0 ? (otherWins / otherTotal) * 100 : 0;
                  return (
                    <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20 overflow-hidden group hover:border-emerald-500/40 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                              <TrendingUp className="w-6 h-6 text-emerald-400" />
                            </div>
                            <div>
                              <h3 className="font-bold text-lg">{language === 'fr' ? 'Autres' : 'Others'}</h3>
                              <p className="text-xs text-muted-foreground">
                                {otherTotal} {language === 'fr' ? 'analyses' : 'analyses'}
                                <span className="block opacity-70">
                                  {language === 'fr' ? 'ETF · Matières 1res · Indices' : 'ETF · Commodities · Indices'}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="text-4xl font-bold text-emerald-400 mb-2">
                          {otherPct.toFixed(0)}%
                        </div>
                        <div className="h-2 bg-emerald-500/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-1000"
                            style={{ width: `${otherPct}%` }}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
              </div>
            </div>
          </section>

          {/* Performance by Risk Profile */}
          <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent to-black/20">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  {language === 'fr' ? 'Performance par Profil de Risque' : 'Performance by Risk Profile'}
                </h2>
                <p className="text-muted-foreground">
                  {language === 'fr' 
                    ? 'Notre IA adapte ses recommandations selon votre profil. Voici les résultats pour chaque approche.' 
                    : 'Our AI adapts its recommendations to your profile. Here are the results for each approach.'}
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-6">
                {/* Conservative */}
                <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20 text-center p-6">
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="font-bold text-xl text-blue-400 mb-1">
                    {language === 'fr' ? 'Prudent' : 'Conservative'}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4">Levier 10x • TP proche</p>
                  <div className="text-4xl font-bold mb-2">
                    {stats.by_profile.conservative?.total > 0 
                      ? ((stats.by_profile.conservative.wins / stats.by_profile.conservative.total) * 100).toFixed(0) 
                      : 0}%
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {stats.by_profile.conservative?.total || 0} {language === 'fr' ? 'analyses' : 'analyses'}
                  </p>
                </Card>

                {/* Balanced */}
                <Card className="relative bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20 text-center p-6 ring-2 ring-green-500/30 ring-offset-2 ring-offset-background">
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white z-10 whitespace-nowrap">
                    <Star className="w-3 h-3 mr-1" />
                    {language === 'fr' ? 'Recommandé' : 'Recommended'}
                  </Badge>
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4 mt-2">
                    <Scale className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="font-bold text-xl text-green-400 mb-1">
                    {language === 'fr' ? 'Équilibré' : 'Balanced'}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4">Levier 20x • TP moyen</p>
                  <div className="text-4xl font-bold mb-2">
                    {stats.by_profile.balanced?.total > 0 
                      ? ((stats.by_profile.balanced.wins / stats.by_profile.balanced.total) * 100).toFixed(0) 
                      : 0}%
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {stats.by_profile.balanced?.total || 0} {language === 'fr' ? 'analyses' : 'analyses'}
                  </p>
                </Card>

                {/* Dynamic */}
                <Card className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/20 text-center p-6">
                  <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
                    <Flame className="w-8 h-8 text-orange-400" />
                  </div>
                  <h3 className="font-bold text-xl text-orange-400 mb-1">
                    {language === 'fr' ? 'Dynamique' : 'Dynamic'}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4">Levier 30x • TP éloigné</p>
                  <div className="text-4xl font-bold mb-2">
                    {stats.by_profile.dynamic?.total > 0 
                      ? ((stats.by_profile.dynamic.wins / stats.by_profile.dynamic.total) * 100).toFixed(0) 
                      : 0}%
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {stats.by_profile.dynamic?.total || 0} {language === 'fr' ? 'analyses' : 'analyses'}
                  </p>
                </Card>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Public Journal - filterable list of every trade */}
      {hasData && <PublicTrackRecordJournal />}

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-8">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary font-medium">
              {language === 'fr' ? '2 analyses gratuites à l\'inscription' : '2 free analyses on signup'}
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            {language === 'fr' 
              ? 'Prêt à trader avec l\'IA ?' 
              : 'Ready to trade with AI?'}
          </h2>

          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {language === 'fr' 
              ? 'Rejoignez des centaines de traders qui utilisent TRADEX AI pour prendre de meilleures décisions. Concepts ICT, analyses multi-timeframe, alertes en temps réel.' 
              : 'Join hundreds of traders using TRADEX AI to make better decisions. ICT concepts, multi-timeframe analysis, real-time alerts.'}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-lg">
                {language === 'fr' ? 'Commencer gratuitement' : 'Start for free'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/subscribe">
              <Button size="lg" variant="outline" className="border-white/20 hover:bg-white/5 px-8 py-6 text-lg">
                <Eye className="w-5 h-5 mr-2" />
                {language === 'fr' ? 'Voir les plans' : 'View plans'}
              </Button>
            </Link>
          </div>

          {/* Trust badges */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              {language === 'fr' ? 'Données sécurisées' : 'Secure data'}
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              {language === 'fr' ? 'Sans engagement' : 'No commitment'}
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              {language === 'fr' ? 'Analyses instantanées' : 'Instant analyses'}
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="py-8 px-4 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center text-xs text-muted-foreground">
          <p>
            {language === 'fr' 
              ? '⚠️ Les performances passées ne préjugent pas des performances futures. Le trading comporte des risques de perte en capital. Ces statistiques sont basées sur des analyses théoriques de notre IA et non sur des trades réels exécutés. TRADEX AI est un outil d\'aide à la décision et ne constitue pas un conseil en investissement.' 
              : '⚠️ Past performance does not guarantee future results. Trading involves risk of capital loss. These statistics are based on theoretical analyses from our AI, not actual executed trades. TRADEX AI is a decision support tool and does not constitute investment advice.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrackRecord;
